import { z } from 'zod';
import {
  CreateAncillarySessionBodySchema,
  BaggageRequestBodySchema,
  SeatPreferenceBodySchema,
  SubmitAncillaryBodySchema,
} from './requests.js';
import { AncillaryDirectionSchema, AncillaryMoneySchema, SeatPreferenceSchema } from './views.js';

/**
 * DTOs das rotas internas (M2M) do modal-session para ancilares — o BFF resolve a
 * identidade do chat JWT e repassa o `customer` já resolvido; o modal-session
 * guarda-o na sessão e o reusa como `requester` das mutations do legado e no card
 * do Kanban. Spec §6.4. As RESPOSTAS são os schemas públicos de `views.ts`.
 */

export const AncillaryCustomerSchema = z
  .object({
    /** Cognito `sub` do chat — dono da sessão, conferido em toda leitura/escrita. */
    userId: z.string().min(1),
    publicId: z.string().min(1),
    vollUserId: z.string().min(1),
    clientId: z.string().min(1),
    name: z.string().min(1),
    email: z.string().min(1).nullable(),
    companyName: z.string().min(1).nullable(),
    /** `policyGetAccessModal.seat` do viajante, o `seatAccess` do lookup do customer-api:
     *  só `true` libera o assento; `false` = negado, `null` = a política não respondeu. */
    seatAccess: z.boolean().nullable(),
  })
  .strict();
export type AncillaryCustomer = z.infer<typeof AncillaryCustomerSchema>;

/** `POST /internal/ancillaries/overview` */
export const InternalAncillaryOverviewBodySchema = z
  .object({ customer: AncillaryCustomerSchema, voucherCode: z.string().min(1) })
  .strict();
export type InternalAncillaryOverviewBody = z.infer<typeof InternalAncillaryOverviewBodySchema>;

/** `POST /internal/ancillary-sessions` */
export const InternalCreateAncillarySessionBodySchema = z
  .object({ customer: AncillaryCustomerSchema, voucherCode: z.string().min(1) })
  .extend(CreateAncillarySessionBodySchema.shape)
  .strict();
export type InternalCreateAncillarySessionBody = z.infer<
  typeof InternalCreateAncillarySessionBodySchema
>;

/** `POST /internal/ancillary-sessions/:sessionId/submit` */
export const InternalSubmitAncillaryBodySchema = z
  .object({ userId: z.string().min(1), submission: SubmitAncillaryBodySchema })
  .strict();
export type InternalSubmitAncillaryBody = z.infer<typeof InternalSubmitAncillaryBodySchema>;

/** `POST /internal/ancillary-sessions/seat-preference` */
export const InternalSeatPreferenceBodySchema = z
  .object({ customer: AncillaryCustomerSchema, voucherCode: z.string().min(1) })
  .extend(SeatPreferenceBodySchema.shape)
  .strict();
export type InternalSeatPreferenceBody = z.infer<typeof InternalSeatPreferenceBodySchema>;

/** `POST /internal/ancillary-sessions/baggage-request` */
export const InternalBaggageRequestBodySchema = z
  .object({ customer: AncillaryCustomerSchema, voucherCode: z.string().min(1) })
  .extend(BaggageRequestBodySchema.shape)
  .strict();
export type InternalBaggageRequestBody = z.infer<typeof InternalBaggageRequestBodySchema>;

/** Um sentido do pedido no aviso: a rota do sentido e o que foi pedido nele. */
const AncillaryUpdateDirectionSchema = z
  .object({
    direction: AncillaryDirectionSchema,
    origin: z.string().min(1).max(10),
    destination: z.string().min(1).max(10),
    /** Assentos pelo mapa, um por voo do sentido. */
    seats: z.array(z.string().min(1).max(8)).max(8).optional(),
    preference: SeatPreferenceSchema.optional(),
    bags: z.number().int().min(1).max(4).optional(),
    /** Peso por mala pedido fora da oferta (a mala de 32 kg). */
    weightKg: z.number().int().positive().optional(),
  })
  .strict();

/**
 * Os desfechos que tocam o sino do viajante (#4107): confirmado, cancelado (recusa, expiração
 * ou cancelamento) e a falha que acionou o nosso time. Cotação, aprovação e emissão em
 * andamento só aparecem no voucher.
 */
export const AncillaryNotifiedStatusSchema = z.enum(['issued', 'canceled', 'failed']);

/**
 * O desfecho de um sentido do pedido, SEM texto nem idioma: o modal-session o emite quando o
 * status derivado do sentido chega a um dos desfechos acima, e o conversation escreve o aviso
 * no idioma do viajante. O fluxo de assento e bagagem é determinístico — não passa por conversa.
 */
export const AncillaryStatusUpdateSchema = z
  .object({
    requestId: z.string().min(1),
    voucherCode: z.string().min(1).max(60),
    kind: z.enum(['seat_map', 'seat_preference', 'baggage_quote', 'baggage_request']),
    directions: z.array(AncillaryUpdateDirectionSchema).min(1).max(2),
    status: AncillaryNotifiedStatusSchema,
    /** O valor que o legado gravou no voucher filho (cotação automática ou manual). */
    legacyAmount: AncillaryMoneySchema.optional(),
  })
  .strict();
export type AncillaryStatusUpdate = z.infer<typeof AncillaryStatusUpdateSchema>;

/**
 * `POST /internal/ancillary-notifications` (conversation, M2M do modal-session): o aviso vira
 * item no sino + push do `sub`, sem conversa; idempotente por (pedido, sentidos, status).
 */
export const AncillaryNotificationPostBodySchema = z
  .object({ sub: z.string().min(1).max(200), update: AncillaryStatusUpdateSchema })
  .strict();
export type AncillaryNotificationPostBody = z.infer<typeof AncillaryNotificationPostBodySchema>;

/**
 * Erros das rotas internas: `{ error: AncillaryErrorCode }` com o mesmo status HTTP
 * de `ANCILLARY_ERROR_HTTP_STATUS`, para o BFF repassar sem tradução.
 */
